import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useEffect, useRef } from "react";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { CartProvider } from "@/contexts/CartContext";
import { LocationProvider } from "@/contexts/LocationContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import CartSidebar from "@/components/cart/CartSidebar";
import BottomNavigation from "@/components/layout/BottomNavigation";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import Stores from "./pages/Stores";
import StoreDetail from "./pages/StoreDetail";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import AuthLayout from "@/pages/auth/AuthLayout";
import Login from "@/pages/auth/Login";
import AdminLogin from "@/pages/auth/AdminLogin";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import OtpLogin from "@/pages/auth/OtpLogin";
import ResetPassword from "@/pages/auth/ResetPassword";
import StaticPageRoute from "./pages/StaticPageRoute";
import RegisterStore from "./pages/auth/RegisterStore";
import RegisterDelivery from "./pages/auth/RegisterDelivery";
import BecomeASellerLanding from "./pages/BecomeASellerLanding";
import BecomeChannelPartner from "./pages/BecomeChannelPartner";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminStores from "@/pages/admin/AdminStores";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminProductDetail from "@/pages/admin/AdminProductDetail";
import AdminProductForm from "@/pages/admin/AdminProductForm";
import AdminStoreDetail from "@/pages/admin/AdminStoreDetail";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminOrderDetail from "@/pages/admin/AdminOrderDetail";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminProfile from "@/pages/admin/AdminProfile";
import AdminChannel from "@/pages/admin/AdminChannel";
import AdminChannelProducts from "@/pages/admin/AdminChannelProducts";
import AdminChannelProductForm from "@/pages/admin/AdminChannelProductForm";
import AdminChannelOrders from "@/pages/admin/AdminChannelOrders";
import AdminDelivery from "@/pages/admin/AdminDelivery";
import DistributorLayout, { ChannelDeskLayout } from "@/components/distributor/DistributorLayout";
import { wholesalerDesk } from "@/lib/channel-desk";
import DistributorDashboard from "@/pages/distributor/DistributorDashboard";
import DistributorCatalogue from "@/pages/distributor/DistributorCatalogue";
import ChannelPlaceOrderPage from "@/pages/distributor/ChannelPlaceOrderPage";
import DistributorStock from "@/pages/distributor/DistributorStock";
import DistributorOrders from "@/pages/distributor/DistributorOrders";
import DistributorCommission from "@/pages/distributor/DistributorCommission";
import DistributorReports from "@/pages/distributor/DistributorReports";
import AdminDeliveryDetail from "@/pages/admin/AdminDeliveryDetail";
import AdminWallet from "@/pages/admin/AdminWallet";
import AdminWalletTransactions from "@/pages/admin/AdminWalletTransactions";
import AdminUserWallet from "@/pages/admin/AdminUserWallet";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminBanners from "@/pages/admin/AdminBanners";
import AdminCmsPages from "@/pages/admin/AdminCmsPages";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminKycApprovals from "@/pages/admin/AdminKycApprovals";
import AdminReports from "@/pages/admin/AdminReports";
import AdminCommissionDistribution from "@/pages/admin/AdminCommissionDistribution";
import AdminRankRewards from "@/pages/admin/AdminRankRewards";
import AdminRewardRequests from "@/pages/admin/AdminRewardRequests";
import StoreLayout from "@/components/store/StoreLayout";
import StoreDashboard from "@/pages/store/StoreDashboard";
import StoreProducts from "@/pages/store/StoreProducts";
import StoreProductForm from "@/pages/store/StoreProductForm";
import StoreProductDetail from "@/pages/store/StoreProductDetail";
import StoreOrders from "@/pages/store/StoreOrders";
import StorePos from "@/pages/store/StorePos";
import StoreEarnings from "@/pages/store/StoreEarnings";
import StoreReceiveBv from "@/pages/store/StoreReceiveBv";
import StoreReports from "@/pages/store/StoreReports";
import AccountReports from "@/pages/account/AccountReports";
import StoreProfile from "@/pages/store/StoreProfile";
import StoreKyc from "@/pages/store/StoreKyc";
import StoreSupplyOrders from "@/pages/store/StoreSupplyOrders";
import AccountLayout from "@/components/account/AccountLayout";
import AccountOverview from "@/pages/account/AccountOverview";
import AccountProfile from "@/pages/account/AccountProfile";
import AccountMemberId from "@/pages/account/AccountMemberId";
import AccountKyc from "@/pages/account/AccountKyc";
import AccountOrders from "@/pages/account/AccountOrders";
import AccountOrderDetail from "@/pages/account/AccountOrderDetail";
import AccountWallet from "@/pages/account/AccountWallet";
import AccountWalletTransactions from "@/pages/account/AccountWalletTransactions";
import AccountReferrals from "@/pages/account/AccountReferrals";
import AccountDirectTeam from "@/pages/account/AccountDirectTeam";
import AccountLevelTeam from "@/pages/account/AccountLevelTeam";
import AccountRewards from "@/pages/account/AccountRewards";
import AccountRankRewards from "@/pages/account/AccountRankRewards";
import AccountAddresses from "@/pages/account/AccountAddresses";
import DeliveryLayout from "@/components/delivery/DeliveryLayout";
import DeliveryDashboard from "@/pages/delivery/DeliveryDashboard";
import DeliveryOrders from "@/pages/delivery/DeliveryOrders";
import DeliveryEarnings from "@/pages/delivery/DeliveryEarnings";
import DeliveryProfile from "@/pages/delivery/DeliveryProfile";
import PanelNotificationsPage from "@/pages/shared/PanelNotificationsPage";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { ThemeProvider } from "@/components/theme-provider";

const TWO_MINUTES = 2 * 60 * 1000;
const CACHE_MAX_AGE = 30 * 60 * 1000;

// Stale-while-revalidate: data is fresh for 2 minutes (no refetch on remount/refocus),
// after that cached data renders instantly while a background refetch updates it.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: TWO_MINUTES,
      gcTime: CACHE_MAX_AGE,
    },
  },
});

// Survives page reloads (per tab; cleared when the tab closes and on logout).
const PERSIST_KEY = "bm-query-cache";
const persister = createSyncStoragePersister({
  storage: typeof window !== "undefined" ? window.sessionStorage : undefined,
  key: PERSIST_KEY,
  throttleTime: 1000,
});

/** Drops all cached API data when the signed-in user changes or signs out. */
const CacheSessionGuard = () => {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const previous = useRef(userId);

  useEffect(() => {
    if (previous.current !== userId) {
      previous.current = userId;
      queryClient.clear();
      persister.removeClient();
    }
  }, [userId]);

  return null;
};

const StorefrontLayout = () => (
  <LocationProvider>
    <div className="flex flex-col min-h-screen pb-16 md:pb-0">
      <Header />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/products" element={<Products />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/stores" element={<Stores />} />
          <Route path="/stores/:id" element={<StoreDetail />} />
          <Route path="/checkout" element={<ProtectedRoute allowedRoles={["customer"]}><Checkout /></ProtectedRoute>} />
          <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
          <Route path="/about" element={<StaticPageRoute />} />
          <Route path="/how-it-works" element={<StaticPageRoute />} />
          <Route path="/become-a-seller" element={<BecomeASellerLanding />} />
          <Route path="/become-a-distributor" element={<BecomeChannelPartner kind="distributor" />} />
          <Route path="/become-a-wholesaler" element={<BecomeChannelPartner kind="wholesaler" />} />
          <Route path="/delivery-partner" element={<StaticPageRoute />} />
          <Route path="/help" element={<StaticPageRoute />} />
          <Route path="/contact" element={<StaticPageRoute />} />
          <Route path="/privacy" element={<StaticPageRoute />} />
          <Route path="/terms" element={<StaticPageRoute />} />
          <Route path="/refund" element={<StaticPageRoute />} />
          <Route path="/page/:slug" element={<StaticPageRoute />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <BottomNavigation />
      <CartSidebar />
    </div>
  </LocationProvider>
);

const App = () => (
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{ persister, maxAge: CACHE_MAX_AGE, buster: "v1" }}
  >
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
    <AuthProvider>
      <CacheSessionGuard />
      <CartProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
                    <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><AdminLayout /></ProtectedRoute>}>
                      <Route index element={<AdminDashboard />} />
                      <Route path="stores" element={<AdminStores />} />
                      <Route path="stores/:id" element={<AdminStoreDetail />} />
                      <Route path="products" element={<AdminProducts />} />
                      <Route path="products/new" element={<AdminProductForm />} />
                      <Route path="products/:id/edit" element={<AdminProductForm />} />
                      <Route path="products/:id" element={<AdminProductDetail />} />
                      <Route path="categories" element={<AdminCategories />} />
                      <Route path="banners" element={<AdminBanners />} />
                      <Route path="cms" element={<AdminCmsPages />} />
                      <Route path="coupons" element={<AdminCoupons />} />
                      <Route path="orders" element={<AdminOrders />} />
                      <Route path="orders/:id" element={<AdminOrderDetail />} />
                      <Route path="users" element={<AdminUsers />} />
                      <Route path="profile" element={<AdminProfile />} />
                      <Route path="network" element={<Navigate to="/admin/distributors" replace />} />
                      <Route path="distributors" element={<AdminChannel level="Distributor" />} />
                      <Route path="distributors/new" element={<Navigate to="/admin/distributors" replace />} />
                      <Route path="wholesalers" element={<AdminChannel level="Wholesaler" />} />
                      <Route path="wholesalers/new" element={<Navigate to="/admin/wholesalers" replace />} />
                      <Route path="retailers" element={<AdminChannel level="Retailer" />} />
                      <Route path="channel-products" element={<AdminChannelProducts />} />
                      <Route path="channel-products/new" element={<AdminChannelProductForm />} />
                      <Route path="channel-products/:id/edit" element={<AdminChannelProductForm />} />
                      <Route path="channel-orders" element={<AdminChannelOrders />} />
                      <Route path="delivery" element={<AdminDelivery />} />
                      <Route path="delivery/:id" element={<AdminDeliveryDetail />} />
                      <Route path="wallet" element={<AdminWallet />} />
                      <Route path="wallet/transactions" element={<AdminWalletTransactions />} />
                      <Route path="wallet/users/:userId" element={<AdminUserWallet />} />
                      <Route path="reports" element={<AdminReports />} />
                      <Route path="reports/:reportId" element={<AdminReports />} />
                      <Route path="income/distribution" element={<AdminCommissionDistribution />} />
                      <Route path="income/all" element={<Navigate to="/admin/reports/all" replace />} />
                      <Route path="income/direct" element={<Navigate to="/admin/reports/direct" replace />} />
                      <Route path="income/level" element={<Navigate to="/admin/reports/level" replace />} />
                      <Route path="income/reward" element={<Navigate to="/admin/reports/reward" replace />} />
                      <Route path="income/cashback" element={<Navigate to="/admin/reports/cashback" replace />} />
                      <Route path="settings" element={<AdminSettings />} />
                      <Route path="settings/:tabId" element={<AdminSettings />} />
                      <Route path="referral-levels" element={<Navigate to="/admin/settings/levels" replace />} />
                      <Route path="email-settings" element={<Navigate to="/admin/settings/email" replace />} />
                      <Route path="integrations" element={<Navigate to="/admin/settings/integrations" replace />} />
                      <Route path="kyc" element={<AdminKycApprovals />} />
                      <Route path="notifications" element={<PanelNotificationsPage scope="admin" />} />
                      <Route path="rank-rewards" element={<AdminRankRewards />} />
                      <Route path="completed-rewards" element={<AdminRewardRequests />} />
                    </Route>

                    <Route path="/distributor" element={<ProtectedRoute allowedRoles={["distributor"]}><DistributorLayout /></ProtectedRoute>}>
                      <Route index element={<DistributorDashboard />} />
                      <Route path="catalogue" element={<DistributorCatalogue />} />
                      <Route path="catalogue/place-order/:id" element={<ChannelPlaceOrderPage />} />
                      <Route path="stock" element={<DistributorStock />} />
                      <Route path="orders" element={<DistributorOrders />} />
                      <Route path="commission" element={<DistributorCommission />} />
                      <Route path="reports" element={<DistributorReports />} />
                      <Route path="reports/:reportId" element={<DistributorReports />} />
                      <Route path="notifications" element={<PanelNotificationsPage scope="me" />} />
                    </Route>
                    <Route path="/wholesaler" element={<ProtectedRoute allowedRoles={["wholesaler"]}><ChannelDeskLayout desk={wholesalerDesk} /></ProtectedRoute>}>
                      <Route index element={<DistributorDashboard />} />
                      <Route path="catalogue" element={<DistributorCatalogue />} />
                      <Route path="catalogue/place-order/:id" element={<ChannelPlaceOrderPage />} />
                      <Route path="stock" element={<DistributorStock />} />
                      <Route path="orders" element={<DistributorOrders />} />
                      <Route path="commission" element={<DistributorCommission />} />
                      <Route path="reports" element={<DistributorReports />} />
                      <Route path="reports/:reportId" element={<DistributorReports />} />
                      <Route path="notifications" element={<PanelNotificationsPage scope="me" />} />
                    </Route>

                    <Route path="/store" element={<ProtectedRoute allowedRoles={["store"]}><StoreLayout /></ProtectedRoute>}>
                      <Route index element={<StoreDashboard />} />
                      <Route path="products" element={<StoreProducts />} />
                      <Route path="products/new" element={<StoreProductForm />} />
                      <Route path="products/:id/edit" element={<StoreProductForm />} />
                      <Route path="products/:id" element={<StoreProductDetail />} />
                      <Route path="orders" element={<StoreOrders />} />
                      <Route path="pos" element={<StorePos />} />
                      <Route path="earnings" element={<StoreEarnings />} />
                      <Route path="bv" element={<StoreReceiveBv />} />
                      <Route path="supply" element={<DistributorCatalogue />} />
                      <Route path="supply/place-order/:id" element={<ChannelPlaceOrderPage />} />
                      <Route path="supply/stock" element={<DistributorStock />} />
                      <Route path="supply/orders" element={<StoreSupplyOrders />} />
                      <Route path="supply/commission" element={<DistributorCommission />} />
                      <Route path="reports" element={<StoreReports />} />
                      <Route path="reports/:reportId" element={<StoreReports />} />
                      <Route path="profile" element={<StoreProfile />} />
                      <Route path="kyc" element={<StoreKyc />} />
                      <Route path="notifications" element={<PanelNotificationsPage scope="me" />} />
                    </Route>

                    <Route path="/account" element={<ProtectedRoute allowedRoles={["customer"]}><AccountLayout /></ProtectedRoute>}>
                      <Route index element={<AccountOverview />} />
                      <Route path="profile" element={<AccountProfile />} />
                      <Route path="member-id" element={<AccountMemberId />} />
                      <Route path="kyc" element={<AccountKyc />} />
                      <Route path="orders" element={<AccountOrders />} />
                      <Route path="orders/:id" element={<AccountOrderDetail />} />
                      <Route path="wallet" element={<AccountWallet />} />
                      <Route path="wallet/transactions" element={<AccountWalletTransactions />} />
                      <Route path="rewards" element={<AccountRewards />} />
                      <Route path="rank-rewards" element={<AccountRankRewards />} />
                      <Route path="referrals" element={<AccountReferrals />} />
                      <Route path="team/direct" element={<AccountDirectTeam />} />
                      <Route path="team/level" element={<AccountLevelTeam />} />
                      <Route path="reports" element={<AccountReports />} />
                      <Route path="reports/:reportId" element={<AccountReports />} />
                      <Route path="income/all" element={<Navigate to="/account/reports/all" replace />} />
                      <Route path="income/direct" element={<Navigate to="/account/reports/direct" replace />} />
                      <Route path="income/level" element={<Navigate to="/account/reports/level" replace />} />
                      <Route path="income/reward" element={<Navigate to="/account/reports/reward" replace />} />
                      <Route path="income/cashback" element={<Navigate to="/account/reports/cashback" replace />} />
                      <Route path="addresses" element={<AccountAddresses />} />
                      <Route path="notifications" element={<PanelNotificationsPage scope="me" />} />
                    </Route>

                    <Route path="/delivery" element={<ProtectedRoute allowedRoles={["delivery"]}><DeliveryLayout /></ProtectedRoute>}>
                      <Route index element={<DeliveryDashboard />} />
                      <Route path="orders" element={<DeliveryOrders />} />
                      <Route path="earnings" element={<DeliveryEarnings />} />
                      <Route path="profile" element={<DeliveryProfile />} />
                      <Route path="notifications" element={<PanelNotificationsPage scope="me" />} />
                    </Route>

                    <Route path="/auth" element={<AuthLayout />}>
                      <Route index element={<Navigate to="login" replace />} />
                      <Route path="login" element={<Login />} />
                      <Route path="admin/secure/login" element={<AdminLogin />} />
                      <Route path="register" element={<Register />} />
                      <Route path="register-store" element={<RegisterStore />} />
                      <Route path="register-delivery" element={<RegisterDelivery />} />
                      <Route path="forgot-password" element={<ForgotPassword />} />
                      <Route path="reset-password" element={<ResetPassword />} />
                      <Route path="otp" element={<OtpLogin />} />
                    </Route>
                      <Route path="/*" element={<StorefrontLayout />} />
                    </Routes>
                  </BrowserRouter>
                </TooltipProvider>
              </CartProvider>
    </AuthProvider>
    </ThemeProvider>
  </PersistQueryClientProvider>
);

export default App;
